import { DateTime } from 'luxon'
import type { UseHeadInput, UseSeoMetaInput } from '@unhead/vue'
import type { Image, Player } from '~/types'

export const usePlayer = (playerRef: MaybeRefOrGetter<Maybe<Player.Model | undefined>>) => {
  const player = ref<Maybe<Player.Model>>()

  const id = computed<Maybe<number>>(() => player.value?.id || null)
  const firstName = computed<Maybe<string>>(() => player.value?.firstName || null)
  const lastName = computed<Maybe<string>>(() => player.value?.lastName || null)
  const fullName = computed<Maybe<string>>(() => player.value?.fullName || null)
  const jerseyNumber = computed<Maybe<string>>(() => player.value?.jerseyNumber || null)
  const position = computed<Maybe<string>>(() => player.value?.position || null)
  const height = computed<Maybe<string>>(() => player.value?.height || null)
  const weight = computed<Maybe<string>>(() => {
    if (!player.value?.weight) {
      return null
    }

    return parseInt(player.value.weight).toString()
  })
  const college = computed<Maybe<string>>(() => player.value?.college || null)
  const birthPlace = computed<Maybe<string>>(() => player.value?.birthPlace || null)
  const birthDate = computed<Maybe<string>>(() => player.value?.birthDate || null)
  const age = computed<Maybe<string>>(() => {
    if (!birthDate.value) {
      return null
    }
    const diff = DateTime.fromISO(birthDate.value).diffNow('years').years
    return `${Math.abs(Math.ceil(diff))}`
  })

  const heightFeet = computed<Maybe<string>>(() => {
    if (!height.value) {
      return null
    }

    const heightInches = parseFloat(height.value)
    const feet = Math.floor(heightInches / 12)
    const inches = heightInches % 12

    return `${feet}'${inches}"`
  })

  const photo = computed<Maybe<Image>>(() => player.value?.photo || null)
  const slug = computed<Maybe<string>>(() => player.value?.slug || null)

  const headInput = computed<UseHeadInput>(() => {
    if (!player.value) {
      return {}
    }

    return {
      title: fullName.value,
    }
  })

  const seoMetaInput = computed<UseSeoMetaInput>(() => (
    {
      title: fullName.value,
      // TODO: Add meta data
      // ogTitle: metaTitle || name.value,
      // description: metaDescription.value,
      // ogDescription: metaDescription.value,
      // ogImage: logo.value?.src,
    }
  ))

  const biography = computed<Maybe<string>>(() => player?.value?.biography || null)
  const previousExperience = computed<Maybe<string>>(() => player.value?.previousExperience || null)
  const previousExperienceTitle = computed<string>(() => player.value?.previousExperienceTitle || 'Previous Experience')

  const facebookLink = computed<Maybe<string>>(() => player.value?.facebookLink || null)
  const instagramLink = computed<Maybe<string>>(() => player.value?.instagramLink || null)
  const tiktokLink = computed<Maybe<string>>(() => player.value?.tiktokLink || null)
  const xLink = computed<Maybe<string>>(() => player.value?.xLink || null)
  const youtubeLink = computed<Maybe<string>>(() => player.value?.youtubeLink || null)

  watchEffect(() => {
    player.value = toValue(playerRef)
  })

  return {
    age,
    biography,
    birthDate,
    birthPlace,
    college,
    facebookLink,
    firstName,
    fullName,
    headInput,
    height,
    heightFeet,
    id,
    instagramLink,
    jerseyNumber,
    lastName,
    photo,
    player,
    position,
    previousExperience,
    previousExperienceTitle,
    seoMetaInput,
    slug,
    tiktokLink,
    weight,
    xLink,
    youtubeLink,
  }
}
